import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import usePayment from '../../hooks/usePayment';

interface CampaignContextPayload {
  packageId?: string | null;
  packageName?: string | null;
  adId?: string | null;
}

interface DemoPaymentFormProps {
  onAmountChange?: (amount: number | null) => void;
  initialAmount?: number | null;
  campaignContext?: CampaignContextPayload | null;
}

const DemoPaymentForm: React.FC<DemoPaymentFormProps> = ({
  onAmountChange,
  initialAmount,
  campaignContext,
}) => {
  const [amount, setAmount] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { processPayment, error: remoteError, paymentResult } = usePayment();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (typeof initialAmount === 'number' && initialAmount > 0) {
      const value = String(initialAmount);
      setAmount(value);
      onAmountChange?.(initialAmount);
    }
  }, [initialAmount, onAmountChange]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setLocalError('Please enter a valid amount.');
      return;
    }

    setSubmitting(true);
    try {
      const payment = await processPayment({
        amount: numericAmount,
        demoMode: true,
        paymentMethod: 'demo',
        campaignPackageId: campaignContext?.packageId ?? undefined,
        campaignPackageName: campaignContext?.packageName ?? undefined,
        campaignAdId: campaignContext?.adId ?? undefined,
      });

      if (!payment) {
        setLocalError('Demo payment could not be processed. Please try again.');
        return;
      }

      if (typeof window !== 'undefined') {
        try {
          window.dispatchEvent(new CustomEvent('tellurvision-payment-success'));
        } catch {
          // ignore event dispatch failures
        }
      }

      setAmount('');
      onAmountChange?.(null);

      navigate('/payment/confirmation', {
        state: {
          amount: numericAmount,
          currency: payment.currency || 'USD',
          packageName: campaignContext?.packageName ?? null,
          packageId: campaignContext?.packageId ?? null,
          adId: campaignContext?.adId ?? null,
          referenceId: payment._id ?? payment.id ?? null,
          createdAt: payment.createdAt ?? payment.date ?? new Date().toISOString(),
          from: location.pathname,
        },
      });
    } finally {
      setSubmitting(false);
    }
  };

  const effectiveError = localError || remoteError || null;
  const showSuccess = !effectiveError && paymentResult && paymentResult.success;

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Demo payment</h2>
        <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
          Stripe is not configured locally, so this environment can record a demo payment and mark
          the campaign funded for testing.
        </p>
      </div>
      {effectiveError && <p className="text-xs text-red-600">{effectiveError}</p>}
      {showSuccess && !effectiveError && (
        <p className="text-xs text-emerald-600">Demo payment recorded successfully.</p>
      )}
      <form onSubmit={handleSubmit} className="mt-2 space-y-3 text-sm">
        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">Amount (USD)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => {
              const next = e.target.value;
              setAmount(next);
              const parsed = Number(next);
              onAmountChange?.(Number.isFinite(parsed) && parsed > 0 ? parsed : null);
            }}
            className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="mt-2 inline-flex items-center justify-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:bg-slate-400"
        >
          {submitting ? 'Processing…' : 'Record demo payment'}
        </button>
      </form>
    </div>
  );
};

export default DemoPaymentForm;